import { PlusCircleOutlined } from '@ant-design/icons'
import { Button, Col, DatePicker, Form, Input, Modal, notification, Row, Select, Spin } from 'antd'
import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useState } from 'react'
import { useGetCategoryOptions } from 'src/hooks/useGetCategoryOptions'
import { useModalState } from 'src/hooks/useModalState'
import { CATEGORY_TYPE, ERROR_TYPE, translateConst } from 'src/libs/constant'
import { CustomAutoComplete as AutoComplete } from 'src/components/common/autocomplete'
import userApi from 'src/api/userApi'
import { copyTruthyValues, getFullName } from 'src/libs/util'
import useGetHourOptionsByType from 'src/hooks/useGetHourOptionsByType'
import { SCHEDULE_TYPE } from 'src/libs/constant/calendar'
import { currentDateWithTimezone } from 'src/libs/date'
import { uniqBy } from 'lodash'
import technicalTicketApi from 'src/api/technicalTicketApi'

const sessionTypeOptions = [
  SCHEDULE_TYPE.DEMO,
  SCHEDULE_TYPE.REGULAR,
  SCHEDULE_TYPE.SUPPORT_TECHNICAL,
].map((value) => ({
  value,
  label: [SCHEDULE_TYPE.DEMO, SCHEDULE_TYPE.REGULAR].includes(value) ? value : 'OTHER',
}))

export function TicketFormHTKT({
  studentInfo,
  setVisibleTooltip = () => {},
  handleReload = () => {},
  isButton = false,
  setDateNow = () => {},
  typeButton = 'primary'
}) {
  const { t } = useTranslation()
  const [typeClass, setTypeClass] = useState()
  const [isSubmitting, setSubmitting] = useState(false)
  const { isModalVisible, handleClose, handleOpen } = useModalState()
  const [form] = Form.useForm()

  const sessionTimeBackup = useGetHourOptionsByType('BACKUP', true)
  const sessionTimeDemo = useGetHourOptionsByType('DEMO', true)
  const sessionTimeRegular = useGetHourOptionsByType('REGULAR', true)

  const sessionTimeOptions = useMemo(() => {
    switch (typeClass) {
      case 'DEMO':
        return sessionTimeDemo
      case 'REGULAR':
        return uniqBy([...sessionTimeRegular, ...sessionTimeBackup], 'value')
      default:
        return uniqBy([...sessionTimeRegular, ...sessionTimeDemo, ...sessionTimeBackup], 'value')
    }
  }, [sessionTimeRegular, sessionTimeDemo, sessionTimeBackup, typeClass])

  const requiredRule = [
    {
      required: true,
      message: t('common.text.field_required'),
    },
  ]

  const deviceOptions = useGetCategoryOptions(CATEGORY_TYPE.DEVICE)
  const { errorTypeOptions } = useMemo(() => {
    return { errorTypeOptions: translateConst(t, ERROR_TYPE, 'error_type') }
  }, [t])

  const openModal = () => {
    handleOpen()
    if (studentInfo) {
      form.setFieldsValue(studentInfo)
    }
    setVisibleTooltip(false)
  }
  const closeModal = () => {
    handleClose()
    form.resetFields()
  }

  const handleSelectType = (value) => {
    form.validateFields(['sessionCode'])
    setTypeClass(value)
  }

  const onCreateOptions = (data) => {
    if (!data || !data.length) {
      const { userId } = form.getFieldsValue()
      if (userId) {
        form.resetFields(['userId', 'studentName'])
      }
    }
    return data
  }

  const onChangePhone = () => {
    form.setFieldsValue({
      ...form.getFieldsValue(),
      studentId: '',
      studentName: '',
    })
  }

  const onSelectPhone = (value, option) => {
    if (value) {
      form.setFieldsValue({
        ...form.getFieldsValue(),
        studentId: option.id,
        studentName: option.label,
      })
    }
  }

  const getErrorTypeRules = () => ({
    required: form.getFieldValue('type') === SCHEDULE_TYPE.REGULAR,
    message: 'Bắt buộc nhập với loại ticket là REGULAR',
  })

  const onFinish = async (values) => {
    setSubmitting(true)
    const payload = copyTruthyValues(values, [
      'deviceId',
      'date',
      'studentId',
      'sessionCode',
      'classInId',
      'note',
      'type',
      'classTimeSlotId',
      'phone',
      'errorType',
    ])
    payload.date = Number(values?.date?.format('YYYYMMDD'))
    if (![SCHEDULE_TYPE.DEMO, SCHEDULE_TYPE.REGULAR].includes(payload.type)) {
      payload.type = 'OTHER'
    }
    const { status, data } = await technicalTicketApi.create(payload)
    setSubmitting(false)
    if (status === 200) {
      closeModal()
      handleReload()
      notification.success({
        message: 'Tạo ticket HTKT thàng công!'
      })
      setDateNow(Date.now())
    }
  }

  return (
    <>
      {
        !isButton ? (
          <Button
            className='d-flex justify-content-center align-items-center mr-1'
            icon={!isButton ? <PlusCircleOutlined style={{ fontSize: '20px' }} /> : ''}
            shape='circle'
            type='text'
            onClick={openModal}
          />
        ) : (
          <Button onClick={openModal} type={typeButton} style={{ width: 170 }}>Tạo ticket HTKT</Button>
        )
      }
      
      <Modal
        footer={null}
        title={t('page.ticket.label.create_ticket_HTKT')}
        destroyOnClose
        visible={isModalVisible}
        onCancel={closeModal}
        width={900}
      >
        <Spin spinning={isSubmitting}>
          <Form form={form} {...formItemLayout} labelAlign='left' footer={null} onFinish={onFinish}>
            <Row gutter={10}>
              <Col xs={24} sm={24} md={12}>
                <Form.Item name='phone' label={t('common.label.phone')} rules={requiredRule}>
                  <AutoComplete
                    createOptions={onCreateOptions}
                    fetchOptions={getOptionsData}
                    allowClear
                    onSelect={onSelectPhone}
                    placeholder={t('page.ticket.placeholder.input_phone_to_search')}
                    onChange={onChangePhone}
                    disabled
                  />
                </Form.Item>
                <Form.Item name='studentId' className='d-none'>
                  <Input disabled />
                </Form.Item>
                <Form.Item name='studentName' label={t('page.ticket.label.student')}>
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  name='type'
                  label={t('page.ticket.placeholder.periodical_ticket_type')}
                  rules={requiredRule}
                >
                  <Select options={sessionTypeOptions} onChange={handleSelectType} disabled />
                </Form.Item>
                <Form.Item
                  name='classTimeSlotId'
                  label={t('page.ticket.label.session_time')}
                  rules={requiredRule}
                >
                  <Select options={sessionTimeOptions} disabled />
                </Form.Item>
                <Form.Item
                  name='sessionCode'
                  label={
                    form.getFieldValue('type') === 'REGULAR'
                      ? t('page.ticket.label.session_code')
                      : t('page.ticket.label.class_code')
                  }
                  rules={
                    [SCHEDULE_TYPE.DEMO, SCHEDULE_TYPE.REGULAR].includes(
                      form.getFieldValue('type'),
                    ) && requiredRule
                  }
                  dependencies={['sessionTime']}
                >
                  <Input disabled />
                </Form.Item>
                <Form.Item name='classInId' label={t('common.label.class_in_id')}>
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col xs={24} sm={24} md={12}>
                <Form.Item
                  name='date'
                  label={t('page.ticket.label.support_date')}
                  rules={requiredRule}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    placeholder=''
                    format='DD/MM/YYYY'
                    disabledDate={(date) => {
                      return date.valueOf() < currentDateWithTimezone().setHours(0, 0, 0, 0)
                    }}
                  />
                </Form.Item>
                <Form.Item
                  name='deviceId'
                  label={t('page.ticket.label.device')}
                  rules={requiredRule}
                >
                  <Select options={deviceOptions} />
                </Form.Item>
                <Form.Item
                  name='errorType'
                  label={t('page.ticket.label.error_type')}
                  rules={[getErrorTypeRules]}
                >
                  <Select options={errorTypeOptions} />
                </Form.Item>
                <Form.Item
                  name='note'
                  label={t('page.ticket.label.description')}
                  rules={requiredRule}
                >
                  <Input.TextArea />
                </Form.Item>
              </Col>
              <Col
                span={24}
                style={{
                  textAlign: 'center',
                }}
              >
                <Button className='mr-2' onClick={closeModal}>
                  {t('common.label.cancel')}
                </Button>
                <Button type='primary' htmlType='submit'>
                  {t('common.label.save')}
                </Button>
              </Col>
            </Row>
          </Form>
        </Spin>
      </Modal>
    </>
  )
}

async function getOptionsData(input) {
  input = input?.replace(/^0/, '')
  if (!input || !/^\d+$/.test(input)) {
    return
  }
  const { data, status } = await userApi.findStudentsByPhone(input)
  if (status === 200) {
    if (data) {
      return data.map((student, idx) => {
        const fullName = getFullName(student)
        const value = data.length === 1 ? student.phone : `${student.phone}-${idx + 1}`
        return { value, label: fullName, id: student.id }
      })
    }
  }
}

export const formItemLayout = {
  labelCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 8,
    },
  },
  wrapperCol: {
    xs: {
      span: 24,
    },
    sm: {
      span: 14,
    },
  },
}
